<script setup>
// Global consent gate. Every cookie Pandig needs to work (session_id, the Rails
// session cookie, sidebar_state) is strictly functional and is set regardless, so the
// first row is shown on and cannot be switched off. The two optional rows start off
// and stay off until this card is saved with one of them on (lib/analytics.js,
// Privacybeleid.vue §5). Neither switch is preselected, Alles weigeren and Alles
// accepteren are one click and identically styled, and there is deliberately no way to
// dismiss the card without answering — a dismissal is not consent.
//
// Those are law rather than taste, and this is the note to read before changing them.
// A pre-ticked switch is not the active act art. 11.7a Tw and Planet49 (C-673/17)
// require. Giving either all-in-one button more visual weight than the other is
// deceptive design under EDPB 03/2022 §3.1. And with both switches off, an accept
// that costs three clicks against a refusal that costs one fails the same symmetry
// from the other side, which is why Alles accepteren exists at all (AP, "Heldere
// cookiebanners"). Any of the three makes the consent collected here invalid, which
// costs exactly the measurement it was meant to win.
//
// Wanting more yes is legitimate. The place to spend it is the copy (i18n
// cookies.body), which may say what a yes buys, not the defaults or the buttons.
import { useResizeObserver } from "@vueuse/core";
import { computed, nextTick, ref, watch, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { buttonVariants } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { acceptAll, analyticsConfigured, consentAnswered, denyAll, marketingAccepted, noticeReopened, saveConsent, statisticsAccepted } from "@/lib/analytics";

const { t } = useI18n();
// No configured id means nothing to consent to, so there is no card to answer — that
// is how development and test stay quiet. Read once: the server renders the ids into
// the layout, and they cannot change while the page is open.
const configured = analyticsConfigured();
// Open until answered, and again whenever the footer's "Cookievoorkeuren" link asks
// for it — that link is how §9 promises consent can be withdrawn.
const open = computed(() => configured && (!consentAnswered.value || noticeReopened.value));
const card = ref(null);

// Drafts, not the stored answer: flicking a switch must measure nothing until Opslaan
// is clicked. Reopened from the footer they start at the standing answer, so the
// switches themselves answer "where do I stand?" instead of a sentence about it.
const statistics = ref(false);
const marketing = ref(false);

// Layer one is the two whole-answer buttons; the per-category switches sit behind Zelf
// kiezen. Not to bury them — refusing and accepting both stay on the first layer, which
// is the part that has to be equal — but because three rows and their descriptions make
// this card half a phone screen tall, floating over whichever full-screen panel the
// visitor was reading. Someone who came back through "Cookievoorkeuren" is here for
// exactly those switches, so a standing answer opens them.
const detailed = ref(false);
// Each row's text is a <label> for its switch, so the whole row is the target rather
// than the 36x20 switch — WCAG 2.2 asks for 24x24 and a thumb that small is a miss on
// a phone besides. `for` rather than wrapping: a control inside its own label receives
// the click twice and toggles straight back.
const STATISTICS_ID = "cookie-statistics";
const MARKETING_ID = "cookie-marketing";

watch(open, async (value) => {
  if (!value) return reserve("");

  statistics.value = statisticsAccepted.value;
  marketing.value = marketingAccepted.value;
  detailed.value = consentAnswered.value;

  // Reopened on purpose through the footer's "Cookievoorkeuren": nothing else on the
  // page changed, so someone reading by keyboard or screen reader would never learn the
  // card came back — and §9's promise that withdrawing is as easy as giving depends on
  // finding it. Take focus there. The first-visit card deliberately does not: that one
  // arrives unasked, and pulling focus out of a sentence someone is mid-way through is
  // its own kind of rude.
  if (!noticeReopened.value) return;
  await nextTick();
  card.value?.focus();
}, {immediate: true});

// Opening the switches replaces the button that was clicked, so there is nothing left
// to announce the rows that just appeared. Move to the first one that is actually a
// choice — Noodzakelijk is disabled and takes no focus.
async function customise() {
  detailed.value = true;
  await nextTick();
  document.getElementById(STATISTICS_ID)?.focus();
}

// It's `fixed`, so it floats over whatever real content — a footer's links, a page's
// own final CTA — happens to be at the bottom of whichever route is mounted; there's
// no one corner that's safe on every route. Reserve that much space in the document
// instead, so the card only ever hovers over blank space.
useResizeObserver(card, ([entry]) => {
  reserve(`${entry.target.getBoundingClientRect().height + 16}px`);
});
onUnmounted(() => reserve(""));

function save() {
  saveConsent({statistics: statistics.value, marketing: marketing.value});
}

// Reserving the space as body padding isn't enough on the marketing page: it snaps the
// footer's own bottom edge flush to the bottom of the viewport, which scrolls straight
// past anything below the footer and lands the footer's links underneath this card.
// Publish the reserved height too, so that snap point can keep clear of it
// (html.snap-pages footer in application.css).
function reserve(height) {
  document.body.style.paddingBottom = height;
  document.documentElement.style.setProperty("--notice-h", height || "0px");
}
</script>

<template>
  <div
    v-if="open"
    ref="card"
    role="region"
    tabindex="-1"
    :aria-label="t('cookies.eyebrow')"
    class="fixed inset-x-3 bottom-3 z-40 max-w-sm border-2 border-foreground bg-card p-3 shadow-[var(--shadow-hard)] outline-none sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-md sm:p-4"
  >
    <p class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-primary sm:text-[0.6875rem]">{{ t("cookies.eyebrow") }}</p>
    <!-- Notably tighter on a phone: this floats over whichever full-screen panel the
         visitor happens to be on, and at desktop sizing it covered a third of one. It
         also stands down on a phone once the categories are open, where it would be
         summarising rows the visitor can now read in full — and where keeping both is
         the difference between a card that fits a short screen and one that doesn't. -->
    <p class="mt-1 text-xs leading-snug text-muted-foreground sm:mt-1.5 sm:text-sm sm:leading-relaxed" :class="{'max-sm:hidden': detailed}">
      {{ t("cookies.body") }}
    </p>
    <p v-if="consentAnswered" class="mt-1.5 text-xs font-semibold text-foreground sm:text-sm">{{ t("cookies.standing") }}</p>

    <!-- Every row is on screen at once, at every width. What a category does is the
         information the consent is supposed to rest on, so it is the last thing to hide
         on a phone — and a scroll window here proved worse than a tall card: the cut
         landed on a divider, so Marketing simply wasn't there. Chromium draws no
         scrollbar to argue otherwise. The room comes from shorter descriptions and the
         paragraph above standing down instead. -->
    <ul v-if="detailed" class="mt-2.5 divide-y-2 divide-foreground border-y-2 border-foreground sm:mt-3">
      <li class="flex items-start justify-between gap-3 py-2.5">
        <div>
          <p class="text-xs font-semibold text-foreground sm:text-sm">{{ t("cookies.essential_label") }}</p>
          <p class="mt-0.5 text-xs leading-snug text-muted-foreground">{{ t("cookies.essential_description") }}</p>
        </div>
        <!-- On and disabled rather than left out: a strictly necessary cookie is not a
             choice, and saying so where the choices are beats letting the visitor
             wonder which of their cookies this card is quietly not mentioning. No
             label wrapper here — a row that cannot be switched must not feel like it. -->
        <Switch :model-value="true" disabled :aria-label="t('cookies.essential_label')" class="mt-0.5 shrink-0" />
      </li>
      <li class="flex items-start justify-between gap-3 py-2.5">
        <label :for="STATISTICS_ID" class="cursor-pointer">
          <span class="block text-xs font-semibold text-foreground sm:text-sm">{{ t("cookies.statistics_label") }}</span>
          <span class="mt-0.5 block text-xs leading-snug text-muted-foreground">{{ t("cookies.statistics_description") }}</span>
        </label>
        <Switch :id="STATISTICS_ID" v-model="statistics" :aria-label="t('cookies.statistics_label')" class="mt-0.5 shrink-0" />
      </li>
      <li class="flex items-start justify-between gap-3 py-2.5">
        <label :for="MARKETING_ID" class="cursor-pointer">
          <span class="block text-xs font-semibold text-foreground sm:text-sm">{{ t("cookies.marketing_label") }}</span>
          <span class="mt-0.5 block text-xs leading-snug text-muted-foreground">{{ t("cookies.marketing_description") }}</span>
        </label>
        <Switch :id="MARKETING_ID" v-model="marketing" :aria-label="t('cookies.marketing_label')" class="mt-0.5 shrink-0" />
      </li>
    </ul>

    <!-- Its own line rather than trailing the paragraph, so it sits in the same place
         on both layers — and so the layer that drops the paragraph keeps it. -->
    <p class="mt-2 text-xs sm:mt-2.5">
      <!-- Underlined and in the primary, not bold ink. Inside the paragraph it could
           borrow the sentence's context; standing on its own above two buttons it has
           to say "link" without being hovered first. -->
      <RouterLink to="/privacybeleid" class="font-semibold text-primary underline underline-offset-4">{{ t("cookies.link") }}</RouterLink>
    </p>

    <!-- The two one-click answers share a row, a variant and a width: whichever way a
         visitor leans, it costs the same gesture. Saving a mixed answer is the third
         path, so it sits apart and quieter — that asymmetry is between granular and
         all-in-one, never between yes and no. -->
    <div class="mt-2.5 flex gap-2 sm:mt-3">
      <button type="button" :class="buttonVariants({ size: 'sm', variant: 'outline' })" class="flex-1" @click="denyAll">
        {{ t("cookies.decline") }}
      </button>
      <button type="button" :class="buttonVariants({ size: 'sm', variant: 'outline' })" class="flex-1" @click="acceptAll">
        {{ t("cookies.accept") }}
      </button>
    </div>
    <!-- One quiet slot under them, for whichever granular step is next: open the
         categories, then save what they were set to. Not marked up as a disclosure
         (aria-expanded/aria-controls): this button replaces itself with the save, so
         there is never a moment where it sits there expanded. Moving focus into the
         first switch is what announces the rows instead. -->
    <button
      v-if="!detailed"
      type="button"
      :class="buttonVariants({ size: 'sm', variant: 'ghost' })"
      class="mt-1 w-full text-muted-foreground hover:text-foreground"
      @click="customise"
    >
      {{ t("cookies.customise") }}
    </button>
    <button v-else type="button" :class="buttonVariants({ size: 'sm', variant: 'ghost' })" class="mt-1 w-full text-muted-foreground hover:text-foreground" @click="save">
      {{ t("cookies.save") }}
    </button>
  </div>
</template>
